// File: frontend/src/components/messages/MessageBubble.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { MessageDto } from "../../models/MessageModel";
import { parseServerDate } from "../../utils/dateUtils";
import { entryPermalinkPath } from "../../utils/canonicalUrl";

interface MessageBubbleProps {
  message: MessageDto;
  isOwn: boolean;
  /** Called when user clicks the delete button — opens confirmation modal in ConversationView */
  onDeleteRequest: (messageId: number) => void;
  /** Called when user clicks the report button — opens MessageReportModal in ConversationView */
  onReportRequest: (messageId: number) => void;
  /** True if this message has already been reported by the current user */
  alreadyReported?: boolean;
  /** Called when user clicks the reply button — arms the quote composer in ConversationView */
  onReplyRequest: (message: MessageDto) => void;
  /** Called when user clicks the quote block — scrolls to the quoted message */
  onQuoteClick: (messageId: number) => void;
  /** ID of the logged-in user; used to label the quote author as "you" */
  currentUserId?: number | null;
  /** True while this bubble is being flashed after a jump from a quote block */
  isHighlighted?: boolean;
}

// ─── Markup patterns (same grammar as EntryRow) ───────────────────────────────

// (ref: label||matchId) — match reference with Turkish label
const REF_NEW_PATTERN = /\(ref:\s*(.*?)\|\|(\d+)\)/g;
// (ref: slug--id) or (bkz: slug--id) — legacy backward-compat
const REF_LEGACY_PATTERN = /\((ref|bkz):\s*([^\s)]+)\)/g;
// (b: text) — bold
const BOLD_PATTERN = /\(b:\s*(.*?)\)/g;
// (i: text) — italic
const ITALIC_PATTERN = /\(i:\s*(.*?)\)/g;
// (link: url|display text) — external link routed through warning page
const LINK_PATTERN = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
// (#id) — entry reference, e.g. the draft added by an entry's "send message" action
const ENTRY_REF_PATTERN = /\(#(\d+)\)/g;

/**
 * Renders a (#id) entry reference inside a message bubble as a link to that entry.
 *
 * The id on its own does not say which of the five topic types (match, person,
 * club, team, custom topic) the entry belongs to, so the click goes through
 * the /entry/{id} permalink route — the same URL the entry share menu
 * produces. Nothing is resolved at render time, so no request is made for
 * messages the user never interacts with.
 */
const MessageEntryIdLink: React.FC<{ entryId: string }> = ({ entryId }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Straight to the permalink route. Fetching the entry here to assemble a
    // topic URL could not produce a page number, so a reference to an entry on
    // page 4 opened page 1 with nothing to scroll to; /entry/{id} resolves the
    // topic and the page together, for this reader's page size.
    navigate(entryPermalinkPath(Number(entryId)));
  };

  return (
    <a
      href={`#entry-${entryId}`}
      className="msg-ref-link"
      title={`#${entryId}`}
      onClick={handleClick}
    >
      (#{entryId})
    </a>
  );
};

interface ContentSpan {
  index: number;
  length: number;
  node: React.ReactNode;
}

/**
 * Collect all formatted spans from a raw content string.
 * Returns spans sorted ascending by position so renderMessageContent
 * can interleave them with plain-text slices.
 */
function collectSpans(content: string): ContentSpan[] {
  const spans: ContentSpan[] = [];
  let m: RegExpExecArray | null;

  // --- ref: new format (ref: label||matchId) ---
  REF_NEW_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = REF_NEW_PATTERN.exec(content)) !== null) {
    const label = m[1].trim();
    const matchId = m[2];
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`ref-${m.index}`}
          to={`/mac--${matchId}`}
          className="msg-ref-link"
          title={`maça git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- ref: legacy format (ref/bkz: slug--id) ---
  REF_LEGACY_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = REF_LEGACY_PATTERN.exec(content)) !== null) {
    if (spans.some((s) => s.index === m!.index)) continue;
    const slug = m[2];
    const label = slug.replace(/--\d+$/, "").replace(/-/g, " ");
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`legacy-${m.index}`}
          to={`/${slug}`}
          className="msg-ref-link"
          title={`maça git: ${label}`}
        >
          {label}
        </Link>
      ),
    });
  }

  // --- bold: (b: text) ---
  BOLD_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = BOLD_PATTERN.exec(content)) !== null) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <strong key={`b-${m.index}`}>{m[1]}</strong>,
    });
  }

  // --- italic: (i: text) ---
  ITALIC_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = ITALIC_PATTERN.exec(content)) !== null) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <em key={`i-${m.index}`}>{m[1]}</em>,
    });
  }

  // --- link: (link: url|display text) — routed through external link warning page ---
  LINK_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = LINK_PATTERN.exec(content)) !== null) {
    const url = m[1].trim();
    const displayText = m[2].trim();
    const warningPath = `/dis-link?url=${encodeURIComponent(url)}`;
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <Link
          key={`link-${m.index}`}
          to={warningPath}
          className="msg-ext-link"
          title={url}
        >
          {displayText}
        </Link>
      ),
    });
  }

  // --- entry reference: (#id) ---
  ENTRY_REF_PATTERN.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  while ((m = ENTRY_REF_PATTERN.exec(content)) !== null) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <MessageEntryIdLink key={`entryref-${m.index}`} entryId={m[1]} />,
    });
  }

  spans.sort((a, b) => a.index - b.index);
  return spans;
}

/**
 * Convert raw message content into React nodes.
 * Inline markup tags are replaced with appropriate elements;
 * the rest is rendered as plain text.
 */
function renderMessageContent(content: string): React.ReactNode[] {
  const spans = collectSpans(content);
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const span of spans) {
    if (span.index > lastIndex) {
      nodes.push(content.slice(lastIndex, span.index));
    }
    nodes.push(span.node);
    lastIndex = span.index + span.length;
  }

  if (lastIndex < content.length) {
    nodes.push(content.slice(lastIndex));
  }

  return nodes;
}

/**
 * Flatten a quoted message down to plain text for the quote block.
 *
 * The quote is itself a button, so the markup cannot be rendered as React nodes
 * here: an <a> inside a <button> is invalid HTML and would swallow the click that
 * is supposed to jump to the original. Instead the markup tokens are unwrapped to
 * their visible label — (b: x) becomes x, (link: url|text) becomes text — so the
 * reader sees the same words as in the original bubble, just without the styling.
 */
function stripMessageMarkup(content: string): string {
  return content
    .replace(REF_NEW_PATTERN, (_full, label: string) => label.trim())
    .replace(REF_LEGACY_PATTERN, (_full, _tag: string, slug: string) =>
      slug.replace(/--\d+$/, "").replace(/-/g, " ")
    )
    .replace(BOLD_PATTERN, (_full, text: string) => text)
    .replace(ITALIC_PATTERN, (_full, text: string) => text)
    .replace(LINK_PATTERN, (_full, _url: string, text: string) => text.trim())
    .trim();
}

/**
 * Renders a single message bubble.
 *
 * Tick logic:
 *   - Own message, not yet read  → single ✓ (sent/delivered)
 *   - Own message, read by other → double ✓✓ (seen)
 *   - Not own message            → no tick
 *
 * Delete button: clicking it calls onDeleteRequest(messageId) which opens
 * a confirmation modal in the parent ConversationView — does NOT delete immediately.
 * No title prop to avoid native browser tooltip.
 *
 * Report button: only visible on other user's messages. Clicking calls
 * onReportRequest(messageId) which opens MessageReportModal in ConversationView.
 * Hidden once the message has already been reported (alreadyReported=true).
 *
 * Reply button: available on both sides. Clicking calls onReplyRequest(message),
 * which arms the composer in ConversationView with this message as the quote target.
 *
 * Quote block: when this message IS a reply, the quoted snippet is rendered above
 * the content. Clicking it calls onQuoteClick(replyToId); ConversationView scrolls
 * to the original, loading older pages first if it is not on screen yet.
 *
 * The wrapper carries id="message-{id}" — that anchor is what the scroll-to-quote
 * lookup in ConversationView resolves.
 */
export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  onDeleteRequest,
  onReportRequest,
  alreadyReported = false,
  onReplyRequest,
  onQuoteClick,
  currentUserId = null,
  isHighlighted = false,
}) => {
  const { t } = useTranslation();

  /*
   * The bubble shows the time only. The day is carried by the separators the
   * conversation view draws between days, so repeating the full date under every
   * single balloon was the same string over and over — noise that also made the
   * bubbles taller than they needed to be.
   */
  const formatTime = (dateStr: string): string => {
    const date = parseServerDate(dateStr);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Full date and time, shown on hover. Nothing is lost by shortening the visible
   * stamp: the exact moment is still one hover away.
   */
  const formatDateTime = (dateStr: string): string => {
    const date = parseServerDate(dateStr);
    // Show full date (day month year) + time so users can see exactly when a message was sent
    return date.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Label above the quoted snippet: "siz" for your own message, otherwise the username
  const quoteAuthorLabel =
    currentUserId !== null && message.replyToSenderId === currentUserId
      ? t("messages_reply_you")
      : message.replyToSenderUsername ?? "";

  return (
    <div
      id={`message-${message.id}`}
      className={`message-bubble-wrapper ${isOwn ? "own" : "other"}`}
    >
      <div className={`message-bubble${isHighlighted ? " message-bubble--highlight" : ""}`}>
        {/*
         * Quote block — only present when this message replies to another one.
         * A truthy check rather than a null check: an older client build (or a
         * cached response) can leave the field undefined, and undefined must not
         * render an empty quote.
         */}
        {message.replyToId ? (
          <button
            type="button"
            className="message-quote"
            onClick={(e) => {
              e.stopPropagation();
              onQuoteClick(message.replyToId!);
            }}
          >
            <span className="message-quote-author">{quoteAuthorLabel}</span>
            <span className="message-quote-text">
              {stripMessageMarkup(message.replyToPreview ?? "")}
            </span>
          </button>
        ) : null}

        {/* Rendered content — supports ref, link, bold, italic markup */}
        <div className="message-content">
          {renderMessageContent(message.content)}
        </div>

        <div className="message-time" title={formatDateTime(message.createdAt)}>
          {formatTime(message.createdAt)}
          {isOwn && (
            <span className="message-read-indicator">
              {/* Single tick = sent; double tick = seen by recipient */}
              {message.read ? " ✓✓" : " ✓"}
            </span>
          )}
        </div>

        {/*
         * Own messages: delete button inside balloon, top-left.
         * The reply button sits right next to it and follows the same hover rule.
         */}
        {isOwn && (
          <button
            className="message-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRequest(message.id);
            }}
          >
            ✕
          </button>
        )}

        {isOwn && (
          <button
            className="message-reply-btn message-reply-btn--own"
            title={t("messages_reply")}
            onClick={(e) => {
              e.stopPropagation();
              onReplyRequest(message);
            }}
          >
            ↩
          </button>
        )}

        {/*
         * Other-user messages: delete + report pinned to top-right
         * INSIDE the balloon, always visible (no hover required).
         */}
        {!isOwn && (
          <div className="message-bubble-actions">
            <button
              className="message-reply-btn"
              title={t("messages_reply")}
              onClick={(e) => {
                e.stopPropagation();
                onReplyRequest(message);
              }}
            >
              ↩
            </button>
            <button
              className="message-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest(message.id);
              }}
            >
              ✕
            </button>
            {/* Report disappears once this message has been reported; reply and delete stay */}
            {!alreadyReported && (
              <button
                className="message-report-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onReportRequest(message.id);
                }}
              >
                ⚑
              </button>
            )}
          </div>
        )}

        {/* Permanent indicator after a successful report */}
        {!isOwn && alreadyReported && (
          <span className="message-reported-indicator">⚑</span>
        )}
      </div>
    </div>
  );
};